import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { Tag } from '../../../../types';
import { tagsRepository } from '../../../../infrastructure/repositories/SupabaseTagsRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';

interface TagsContextData {
    tags: Tag[];
    loading: boolean;
    createTag: (data: Omit<Tag, 'id' | 'createdAt'>) => Promise<void>;
    updateTag: (id: string, data: Partial<Tag>) => Promise<void>;
    deleteTag: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    refreshTags: () => Promise<void>;
}

const TagsContext = createContext<TagsContextData | undefined>(undefined);

export const TagsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [tags, setTags] = useState<Tag[]>([]);
    const [loading, setLoading] = useState(true);

    const loadTags = useCallback(async () => {
        if (!currentOrganization?.id) return;
        setLoading(true);
        try {
            const data = await tagsRepository.findAll(currentOrganization.id);
            setTags(data);
        } catch (error) {
            console.error('Error loading tags:', error);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        loadTags();
    }, [loadTags]);

    const createTag = useCallback(async (data: Omit<Tag, 'id' | 'createdAt'>) => {
        if (!currentOrganization?.id) return;
        try {
            const newTag = await tagsRepository.create(data, currentOrganization.id);
            setTags(prev => [newTag, ...prev]);
        } catch (error) {
            console.error('Error creating tag:', error);
            throw error;
        }
    }, [currentOrganization?.id]);

    const updateTag = useCallback(async (id: string, data: Partial<Tag>) => {
        if (!currentOrganization?.id) return;
        try {
            const updatedTag = await tagsRepository.update(id, data, currentOrganization.id);
            setTags(prev => prev.map(tag => tag.id === id ? updatedTag : tag));
        } catch (error) {
            console.error('Error updating tag:', error);
            throw error;
        }
    }, [currentOrganization?.id]);

    const deleteTag = useCallback(async (id: string) => {
        if (!currentOrganization?.id) return;
        // Optimistic: remove imediatamente da UI
        setTags(prev => prev.filter(tag => tag.id !== id));
        try {
            await tagsRepository.delete(id, currentOrganization.id);
        } catch (error) {
            // Rollback: recarrega se falhar
            console.error('Error deleting tag:', error);
            await loadTags();
            throw error;
        }
    }, [currentOrganization?.id, loadTags]);

    const deleteBulk = useCallback(async (ids: string[]) => {
        if (!currentOrganization?.id || ids.length === 0) return;
        // Optimistic: remove imediatamente da UI
        setTags(prev => prev.filter(tag => !ids.includes(tag.id)));
        try {
            await Promise.all(ids.map(id => tagsRepository.delete(id, currentOrganization.id!)));
        } catch (error) {
            // Rollback: recarrega se falhar
            console.error('Error deleting tags:', error);
            await loadTags();
            throw error;
        }
    }, [currentOrganization?.id, loadTags]);

    return (
        <TagsContext.Provider value={{ tags, loading, createTag, updateTag, deleteTag, deleteBulk, refreshTags: loadTags }}>
            {children}
        </TagsContext.Provider>
    );
};

export const useTagsContext = () => {
    const context = useContext(TagsContext);
    if (!context) throw new Error('useTagsContext must be used within TagsProvider');
    return context;
};
